/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  ActionMenu,
  ActionMenuProps,
  DIALOG_SIZE_SM,
  DROPDOWN_POSITION_RIGHT,
  DialogContentProps,
  DialogProps,
  DropdownItemData,
} from '@squiz/sds';
import { useDialogStore } from 'dxpConsoleCore-mf/State';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import { EVENT_SUBSCRIPTION_BUILDER_DATA_IDS } from '../../forms/EventSubscriptionBuilderForm/EventSubscriptionBuilderForm.constants';
import { EventSubscriptionDeleteForm } from '../../forms/EventSubscriptionDeleteForm/EventSubscriptionDeleteForm';
import { EventSubscriptionDeleteFormProps } from '../../forms/EventSubscriptionDeleteForm/EventSubscriptionDeleteForm.props';
import { EventSubscriptionMenuProps } from './EventSubscriptionMenu.props';

/**
 * The event subscription action menu.
 */
export const EventSubscriptionMenu = (props: EventSubscriptionMenuProps): JSX.Element => {
  const { t } = useTranslation();
  const { subscriptionId, subscriptionName } = props;

  const { updateActiveDialog } = useDialogStore();

  // Labels
  const labels = useMemo(() => {
    return {
      actionMenuAriaLabel: t(CONFIGURATION_STRINGS.eventSubscriptionActionMenuAriaLabel),
      delete: t(CONFIGURATION_STRINGS.delete),
      deleteEventSubscription: t(CONFIGURATION_STRINGS.deleteEventSubscription),
    };
  }, [t]);

  // Delete event subscription menu item
  const deleteDialogProps = {
    dialogContent: {
      renderContent: (props: DialogContentProps): JSX.Element => {
        const formProps: EventSubscriptionDeleteFormProps = {
          ...props,
          subscriptionId,
          subscriptionName,
        };

        return <EventSubscriptionDeleteForm {...formProps} />;
      },
    },
    dialogSize: DIALOG_SIZE_SM,
    heading: labels.deleteEventSubscription,
    scrollOnOverflow: false,
    showDividers: false,
    stateHandler: useDialogStore,
  } as DialogProps;

  const menuItems: Array<DropdownItemData> = [
    {
      action: (): void => updateActiveDialog(deleteDialogProps),
      dataID: EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.deleteButton,
      key: `delete`,
      label: labels.delete,
    },
  ];

  const actionMenuProps: ActionMenuProps = {
    buttonLabel: labels.actionMenuAriaLabel,
    dropdownPosition: DROPDOWN_POSITION_RIGHT,
    itemList: menuItems,
    sectionKey: `event-subscription-action-menu`,
  };

  return (
    <div className="event-subscription-action-menu">
      <ActionMenu {...actionMenuProps} data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.actionMenu} />
    </div>
  );
};
